import { useEffect, useState, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import CompanyDashboard from "@/pages/CompanyDashboard";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import BillingPage from "@/pages/BillingPage";
import MarketingStudio from "@/pages/MarketingStudio";
import CompaniesPage from "@/pages/CompaniesPage";
import CoursesPage from "@/pages/CoursesPage";
import CoursePlayer from "@/pages/CoursePlayer";
import FormsPage from "@/pages/FormsPage";
import FormSubmit from "@/pages/FormSubmit";
import FormSubmissions from "@/pages/FormSubmissions";
import FormBuilder from "@/pages/FormBuilder";
import CalendarPage from "@/pages/CalendarPage";
import CertificatesPage from "@/pages/CertificatesPage";
import ToolboxTalksPage from "@/pages/ToolboxTalksPage";
import ToolboxTalkView from "@/pages/ToolboxTalkView";
import ToolboxTalkBuilder from "@/pages/ToolboxTalkBuilder";
import UserGroupsPage from "@/pages/UserGroupsPage";
import ToolboxTalkAssignments from "@/pages/ToolboxTalkAssignments";
import NotificationSettings from "@/pages/NotificationSettings";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import CourseBuilder from "@/pages/CourseBuilder";
import SSOConfiguration from "@/pages/SSOConfiguration";
import AutoAssignmentRules from "@/pages/AutoAssignmentRules";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'super_admin') {
      return <Navigate to="/admin" replace />;
    } else if (user.role === 'company_admin') {
      return <Navigate to="/company" replace />;
    } else {
      return <Navigate to="/employee" replace />;
    }
  }

  return children;
};

// Dashboard Router - redirects to appropriate dashboard
const DashboardRouter = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (user.role === 'super_admin') {
    return <Navigate to="/admin" replace />;
  } else if (user.role === 'company_admin') {
    return <Navigate to="/company" replace />;
  } else {
    return <Navigate to="/employee" replace />;
  }
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={<DashboardRouter />} />
      
      {/* Super Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <MarketingStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <CompaniesPage />
          </ProtectedRoute>
        }
      />
      
      {/* Company Admin Routes */}
      <Route
        path="/company"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <CompanyDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Employee Routes */}
      <Route
        path="/employee"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'employee']}>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Shared Routes */}
      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            <CoursesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/course/:courseId"
        element={
          <ProtectedRoute>
            <CoursePlayer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms"
        element={
          <ProtectedRoute>
            <FormsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/submit/:templateId"
        element={
          <ProtectedRoute>
            <FormSubmit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/submissions"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <FormSubmissions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/builder"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <FormBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/certificates"
        element={
          <ProtectedRoute>
            <CertificatesPage />
          </ProtectedRoute>
        }
      />
      
      {/* Toolbox Talks Routes */}
      <Route
        path="/toolbox-talks"
        element={
          <ProtectedRoute>
            <ToolboxTalksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolbox-talks/create"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <ToolboxTalkBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolbox-talks/assignments"
        element={
          <ProtectedRoute>
            <ToolboxTalkAssignments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolbox-talks/:talkId"
        element={
          <ProtectedRoute>
            <ToolboxTalkView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-groups"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <UserGroupsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Settings Routes */}
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettings />
          </ProtectedRoute>
        }
      />
      
      {/* Analytics Route */}
      <Route
        path="/analytics"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <AnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Course Builder Route */}
      <Route
        path="/courses/create"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <CourseBuilder />
          </ProtectedRoute>
        }
      />
      
      {/* SSO Configuration Route */}
      <Route
        path="/sso"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SSOConfiguration />
          </ProtectedRoute>
        }
      />
      
      {/* Auto-Assignment Rules Route */}
      <Route
        path="/auto-assign"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <AutoAssignmentRules />
          </ProtectedRoute>
        }
      />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
